import {
  CAPTURE_FIXTURE_SET_NAME,
  STEP_COMMAND_FILE,
  STEP_TEST_CASES_DIR,
  STEP_TEST_CLOCK_SUFFIX,
  STEP_TEST_DIFF_ACTUAL,
  STEP_TEST_DIFF_BEGIN,
  STEP_TEST_DIFF_END,
  STEP_TEST_DIFF_EXPECTED,
  STEP_TEST_OUTPUT_SERIALIZER_FILE,
  STEP_TEST_REQUEST_SERIALIZER_FILE,
  STEP_TEST_REQUESTS_DIVERGED_PREFIX,
  STEP_TEST_REQUESTS_FILE,
  STEP_TEST_RESULTS_SENTINEL,
} from "./constants";
import { STEP_TEST_CONTROL_HOST, STEP_TEST_REQUESTS_SUFFIX } from "./requests";
import {
  type StepTestVolumeFixtureFile,
  bytesAreUtf8Text,
  STEP_TEST_SQLITE_DUMP_SENTINEL,
  STEP_TEST_VOLUME_BASE64_SENTINEL,
} from "./volumes";

export const STEP_TEST_CAPTURE_PURPOSE = "step_test_capture";
export const STEP_TEST_CAPTURE_RUNNER_FILE = "__captureStepTest.sh";
export const STEP_TEST_VOLUME_LIST_FILE = "__stepTestVolumes";
export const STEP_TEST_RO_VOLUME_LIST_FILE = "__stepTestReadOnlyVolumes";
export const STEP_TEST_CAPTURE_SENTINEL = "__3B_STEP_TEST_CAPTURE__";
export const STEP_TEST_FROZEN_CLOCK_HINT =
  "This test was pinned under a frozen clock that replays no longer use. If only timestamps, IDs, or other volatile values differ, add tests/serialize-output (tests/serialize for a volume, or tests/serialize-request when a recorded request differs) that masks only those values, delete this test's .clock file, then rerun the tests.";

// Shared by both scripts so capture and replay never disagree on how a fixture becomes a volume file; references $STORAGE_ROOT, $VOLUME_BASE64_SENTINEL, and $_RO_VOLUMES from each script's preamble.
const VOLUME_SHELL_HELPERS = `_SCRATCH_DIR=$(mktemp -d) || exit 1

_volume_root() {
  printf 'tests/step-tests/%s.volumes' "$1"
}

_clear_volume() {
  _target="$STORAGE_ROOT/$1"
  if [ -d "$_target" ]; then
    find "$_target" -mindepth 1 -exec rm -rf {} +
  else
    mkdir -p "$_target"
  fi
}

_base64_decode() {
  if printf '' | base64 -d >/dev/null 2>&1; then
    base64 -d
  else
    base64 -D
  fi
}

_is_sqlite_file() {
  head -c 15 "$1" 2>/dev/null | grep -q "SQLite format 3"
}

# Orders each table's rows by every column's ordinal position (from pragma_table_info) so two databases with the same logical rows in different insertion order dump byte-identical; fails non-zero on a virtual table or any sqlite3 error.
_sqlite_canonical_dump() {
  _db="$1"
  _out="$2"
  _dump_sql_script="$_SCRATCH_DIR/.step-test-sqlite-dump-$$.sql"
  sqlite3 "$_db" > "$_out" 2>/dev/null <<SQL
.bail on
.output "$_dump_sql_script"
SELECT '.bail on';
SELECT CASE WHEN EXISTS(
  SELECT 1 FROM sqlite_master
  WHERE sql LIKE 'CREATE VIRTUAL TABLE%' AND name NOT LIKE 'sqlite_%'
) THEN 'SELECT FROM;' ELSE '-- no virtual tables' END;
SELECT '.headers off';
SELECT '.mode list';
SELECT 'SELECT ''PRAGMA foreign_keys=OFF;'';';
SELECT 'SELECT ''BEGIN TRANSACTION;'';';
SELECT 'SELECT sql || '';'' FROM sqlite_master WHERE type = ''table'' AND sql IS NOT NULL AND name NOT LIKE ''sqlite_%'' ORDER BY rowid;';
SELECT '.mode insert "' || replace(name, '"', '""') || '"' || char(10) ||
       'SELECT * FROM "' || replace(name, '"', '""') || '" ORDER BY ' ||
       (SELECT group_concat(cid + 1, ',') FROM (
         SELECT cid FROM pragma_table_info(m.name) ORDER BY cid
       )) || ';' || char(10) ||
       '.mode list'
FROM sqlite_master AS m
WHERE type = 'table' AND sql IS NOT NULL AND name NOT LIKE 'sqlite_%'
ORDER BY rowid;
-- Restored AUTOINCREMENT inserts repopulate sqlite_sequence before its explicit rows are restored.
SELECT 'SELECT ''DELETE FROM sqlite_sequence;'';'
WHERE EXISTS(SELECT 1 FROM sqlite_master WHERE name = 'sqlite_sequence');
SELECT '.mode insert sqlite_sequence' || char(10) ||
       'SELECT * FROM sqlite_sequence ORDER BY 1,2;' || char(10) ||
       '.mode list'
WHERE EXISTS(SELECT 1 FROM sqlite_master WHERE name = 'sqlite_sequence');
-- Defer indexes, triggers, and views until after data so restored inserts cannot fire triggers.
SELECT 'SELECT sql || '';'' FROM sqlite_master WHERE type != ''table'' AND sql IS NOT NULL AND name NOT LIKE ''sqlite_%'' ORDER BY rowid;';
SELECT 'SELECT ''COMMIT;'';';
.output stdout
.read "$_dump_sql_script"
SQL
  _dump_status=$?
  return "$_dump_status"
}

_materialize_sqlite_dump() {
  _fixture="$1"
  _target="$2"
  [ -d "\${_target%/*}" ] || mkdir -p "\${_target%/*}" || return 1
  rm -f "$_target"
  sed '1d' "$_fixture" | sqlite3 "$_target"
}

_fixture_to_file() {
  _source="$1"
  _target="$2"
  [ -d "\${_target%/*}" ] || mkdir -p "\${_target%/*}" || return 1
  IFS= read -r _first_line < "$_source"
  if [ "$_first_line" = "$VOLUME_BASE64_SENTINEL" ]; then
    sed '1d' "$_source" | _base64_decode > "$_target"
  elif [ "$_first_line" = "$VOLUME_SQLITE_DUMP_SENTINEL" ]; then
    _materialize_sqlite_dump "$_source" "$_target"
  else
    cp "$_source" "$_target"
  fi
}

_seed_volumes() {
  _root="$(_volume_root "$1")/initial"
  [ -d "$_root" ] || return 0
  for _file in $(find "$_root" -type f | sort); do
    _rel=\${_file#$_root/}
    _target="$STORAGE_ROOT/$_rel"
    _fixture_to_file "$_file" "$_target" || return 1
  done
}

_READ_ONLY_SNAPSHOT_DIR="$_SCRATCH_DIR/.step-test-read-only-snapshot"
_snapshot_read_only_sqlite_files() {
  [ -n "$_RO_VOLUMES" ] || return 0
  _root="$(_volume_root "$1")/initial"
  rm -rf "$_READ_ONLY_SNAPSHOT_DIR"
  for _vol in $_RO_VOLUMES; do
    _fixture_vol="$_root/$_vol"
    [ -d "$_fixture_vol" ] || continue
    for _fixture_file in $(find "$_fixture_vol" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort); do
      IFS= read -r _fixture_first_line < "$_fixture_file"
      [ "$_fixture_first_line" = "$VOLUME_SQLITE_DUMP_SENTINEL" ] || continue
      _rel=\${_fixture_file#$_fixture_vol/}
      _source="$STORAGE_ROOT/$_vol/$_rel"
      [ -f "$_source" ] || continue
      _snapshot="$_READ_ONLY_SNAPSHOT_DIR/$_vol/$_rel"
      mkdir -p "\${_snapshot%/*}" || return 1
      cp "$_source" "$_snapshot" || return 1
    done
  done
}

_volume_message=""
_volume_fail() {
  if [ -z "$_volume_message" ]; then _volume_message="$1"; fi
}

# Compares one volume's on-disk state against a single fixture-phase dir (either
# phase's directory shape is identical, so replay's expected/initial fallback and
# capture's read-only check both drive this one code path); excludes SQLite's
# -wal/-shm from comparison since WAL mode writes a random checkpoint salt into
# -wal and -shm is transient scratch, so neither ever byte-matches between runs.
_compare_volume_against() {
  _vol="$1"
  _fixture_vol="$2"
  if [ -d "$_fixture_vol" ]; then
    for _fixture_file in $(find "$_fixture_vol" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort); do
      _rel=\${_fixture_file#$_fixture_vol/}
      _actual_file_path="$STORAGE_ROOT/$_vol/$_rel"
      _decoded_fixture="$_SCRATCH_DIR/.step-test-expected/$_vol/$_rel"
      IFS= read -r _fixture_first_line < "$_fixture_file"
      if [ ! -f "$_actual_file_path" ]; then
        _volume_fail "missing volume file /storage/$_vol/$_rel"
      elif [ "$3" = "read-only" ] \
        && [ -f "$_READ_ONLY_SNAPSHOT_DIR/$_vol/$_rel" ] \
        && cmp -s "$_READ_ONLY_SNAPSHOT_DIR/$_vol/$_rel" "$_actual_file_path"; then
        :
      elif [ "$_fixture_first_line" = "$VOLUME_SQLITE_DUMP_SENTINEL" ]; then
        _actual_dump="$_SCRATCH_DIR/.step-test-actual-dump/$_vol/$_rel"
        _fixture_db="$_SCRATCH_DIR/.step-test-fixture-db/$_vol/$_rel"
        _fixture_dump="$_SCRATCH_DIR/.step-test-fixture-dump/$_vol/$_rel"
        mkdir -p "\${_actual_dump%/*}" "\${_fixture_db%/*}" "\${_fixture_dump%/*}"
        if ! _sqlite_canonical_dump "$_actual_file_path" "$_actual_dump" \\
          || ! _materialize_sqlite_dump "$_fixture_file" "$_fixture_db" \\
          || ! _sqlite_canonical_dump "$_fixture_db" "$_fixture_dump" \\
          || ! cmp -s "$_fixture_dump" "$_actual_dump"; then
          _volume_fail "volume file /storage/$_vol/$_rel did not match expected content"
        fi
        rm -f "$_actual_dump" "$_fixture_db" "$_fixture_dump"
      elif ! _fixture_to_file "$_fixture_file" "$_decoded_fixture"; then
        _volume_fail "could not prepare expected volume file /storage/$_vol/$_rel"
      elif ! cmp -s "$_decoded_fixture" "$_actual_file_path"; then
        _volume_fail "volume file /storage/$_vol/$_rel did not match expected content"
      fi
    done
  fi
  _actual_vol="$STORAGE_ROOT/$_vol"
  if [ -d "$_actual_vol" ]; then
    for _actual_file in $(find "$_actual_vol" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort); do
      _rel=\${_actual_file#$_actual_vol/}
      if [ ! -f "$_fixture_vol/$_rel" ]; then
        _volume_fail "unexpected volume file /storage/$_vol/$_rel"
      fi
    done
  fi
}

_is_ro_volume() {
  for _ro_vol in $_RO_VOLUMES; do
    [ "$_ro_vol" = "$1" ] && return 0
  done
  return 1
}

_serialize_volume() {
  sh tests/serialize "$1" > "$2"
}`;

// Bun ignores BUN_PRELOAD, so BUN_OPTIONS is the only env var that loads the sandbox preload, which tags replayed requests with the worker’s traceparent, into every bun invocation (env-prefixed, absolute-path, compound).
const BUN_PRELOAD_SHELL = `if [ -n "$BUN_PRELOAD" ]; then
  BUN_OPTIONS="--preload=$BUN_PRELOAD\${BUN_OPTIONS:+ $BUN_OPTIONS}"
  export BUN_OPTIONS
fi`;

const REPLAY_CTL_SHELL_HELPERS = `_check_replay_tools() {
  for _tool in curl jq; do
    if ! command -v "$_tool" >/dev/null 2>&1; then
      printf 'Step-test request replay requires %s. Install curl and jq in the step’s Dockerfile and rebuild its image.\\n' "$_tool" >&2
      return 1
    fi
  done
}

_replay_ctl() {
  if [ -n "$STEP_TEST_CONTROL_URL" ]; then
    _replay_ctl_url="$STEP_TEST_CONTROL_URL/$1?test=$2"
    _replay_ctl_tls_flags=""
  else
    _replay_ctl_url="https://${STEP_TEST_CONTROL_HOST}/$1?test=$2"
    _replay_ctl_tls_flags="--proto =https --tlsv1.2"
  fi
  _replay_ctl_body=$(curl -fsS $_replay_ctl_tls_flags -X POST --max-time 10 "$_replay_ctl_url" 2>/dev/null)
  _replay_ctl_status=$?
  if [ "$_replay_ctl_status" != 0 ] || [ -z "$_replay_ctl_body" ]; then
    printf '%s' "step-test replay control call failed"
    return 0
  fi
  _replay_ctl_message=$(printf '%s' "$_replay_ctl_body" | jq -r '
    if .ok == true then ""
    elif .ok == false then
      (if (.message | type) == "string" and .message != "" then .message
       else "step-test replay control call failed" end)
    else "step-test replay control call failed" end' 2>/dev/null)
  if [ $? -ne 0 ]; then
    printf '%s' "step-test replay control call failed"
    return 0
  fi
  printf '%s' "$_replay_ctl_message"
}`;

// Started only when a test has both a `.requests` fixture and a step-authored
// tests/serialize-request; the proxy can't run step scripts itself, so this
// sh + curl + jq loop long-polls the control host for a live request that
// needs a match key, pipes it through the step's serializer, and posts the
// key back. The long poll itself is the wait; the only sleep is a 1s backoff
// after a curl failure so a dead control channel doesn't hot-spin. The
// serializer always receives the request body base64-encoded
// (bodyEncoding:"base64") — capture-side key computation below uses the same
// contract so keys compare equal.
const NORMALIZE_HELPER_SHELL = `_NORMALIZE_HELPER_PID=""

_normalize_helper_start() {
  (
    _nh_base="\${STEP_TEST_CONTROL_URL:-http://${STEP_TEST_CONTROL_HOST}}"
    _nh_err=$(mktemp) || exit 1
    while :; do
      _nh_resp=$(curl -fsS -X POST --max-time 35 "$_nh_base/normalize/poll" 2>/dev/null)
      if [ $? -ne 0 ]; then
        sleep 1
        continue
      fi
      _nh_id=$(printf '%s' "$_nh_resp" | jq -c '.pending.id // empty' 2>/dev/null)
      [ -n "$_nh_id" ] || continue
      _nh_stdin=$(printf '%s' "$_nh_resp" | jq -c '{
        method: .pending.request.method,
        url: .pending.request.url,
        headers: (.pending.request.headers // []),
        body: (.pending.request.body // ""),
        bodyEncoding: "base64"
      }' 2>/dev/null)
      [ -n "$_nh_stdin" ] || continue
      if _nh_key=$(printf '%s' "$_nh_stdin" | sh tests/serialize-request 2>"$_nh_err"); then
        _nh_reply=$(jq -cn --argjson id "$_nh_id" --arg key "$_nh_key" '{id: $id, key: $key}')
      else
        _nh_reply=$(jq -cn --argjson id "$_nh_id" \\
          --arg error "tests/serialize-request failed: $(head -c 500 "$_nh_err")" \\
          '{id: $id, error: $error}')
      fi
      curl -fsS -X POST --max-time 10 -H 'content-type: application/json' \\
        -d "$_nh_reply" "$_nh_base/normalize/reply" >/dev/null 2>&1
    done
  ) &
  _NORMALIZE_HELPER_PID=$!
}

_normalize_helper_stop() {
  [ -n "$_NORMALIZE_HELPER_PID" ] || return 0
  kill "$_NORMALIZE_HELPER_PID" 2>/dev/null
  wait "$_NORMALIZE_HELPER_PID" 2>/dev/null
  _NORMALIZE_HELPER_PID=""
}

# Runs before \`begin\`: computes each recorded fixture's serializer match key
# (double-run to guard determinism, mirroring the tests/serialize check in
# capture) and registers them with the proxy under fixture set $2 so replay
# matches by key instead of raw method+url+body, leaving them in $_fk_keys.
# Feeds the serializer the same shape as the replay-side normalize helper
# (recorded request headers, body always base64-encoded), so a key computed
# here compares equal to one computed from the live request. Replay recomputes
# keys rather than trusting the pinned ones, so a tests/serialize-request added
# after a test was pinned still applies.
_compute_and_register_fixture_keys() {
  _fk_file="$1"
  _fk_base="\${STEP_TEST_CONTROL_URL:-http://${STEP_TEST_CONTROL_HOST}}"
  _fk_keys="[]"
  _fk_err=$(mktemp) || return 1
  while IFS= read -r _fk_line || [ -n "$_fk_line" ]; do
    [ -n "$_fk_line" ] || continue
    _fk_stdin=$(printf '%s' "$_fk_line" | jq -c '{
      method: .method,
      url: .url,
      headers: (.reqHeaders // []),
      body: (if .reqBodyEncoding == "base64" then (.reqBody // "") else (.reqBody // "" | @base64) end),
      bodyEncoding: "base64"
    }' 2>/dev/null)
    if [ -z "$_fk_stdin" ]; then
      echo "could not parse a recorded request fixture line" >&2
      rm -f "$_fk_err"
      return 1
    fi
    _fk_key1=$(printf '%s' "$_fk_stdin" | sh tests/serialize-request 2>"$_fk_err") || {
      echo "tests/serialize-request failed: $(head -c 500 "$_fk_err")" >&2
      rm -f "$_fk_err"
      return 1
    }
    _fk_key2=$(printf '%s' "$_fk_stdin" | sh tests/serialize-request 2>"$_fk_err") || {
      echo "tests/serialize-request failed: $(head -c 500 "$_fk_err")" >&2
      rm -f "$_fk_err"
      return 1
    }
    if [ "$_fk_key1" != "$_fk_key2" ]; then
      _fk_req=$(printf '%s' "$_fk_line" | jq -r '"\\(.method) \\(.url)"' 2>/dev/null)
      echo "tests/serialize-request produced different output on two runs for $_fk_req; make it deterministic" >&2
      rm -f "$_fk_err"
      return 1
    fi
    _fk_keys=$(printf '%s' "$_fk_keys" | jq -c --arg key "$_fk_key1" '. + [$key]')
  done < "$_fk_file"
  rm -f "$_fk_err"
  if ! printf '%s' "$_fk_keys" | jq -c '{keys: .}' \\
    | curl -fsS -X POST --max-time 10 -H 'content-type: application/json' \\
      -d @- "$_fk_base/fixture-keys?test=$2" >/dev/null 2>&1; then
    echo "failed to register fixture keys with the proxy" >&2
    return 1
  fi
}`;

// Replays each tests/step-tests/<name>.input through the step's real entry
// command and compares stdout to <name>.output ($(...) strips trailing
// newlines on both sides). When the step authors tests/serialize-output, both
// sides are piped through it first so volatile values can be masked. Pure
// POSIX sh — the one runtime present in every step image.
export const STEP_TEST_RUNNER_SH = `#!/bin/sh
SENTINEL="${STEP_TEST_RESULTS_SENTINEL}"
DIFF_BEGIN="${STEP_TEST_DIFF_BEGIN}"
DIFF_EXPECTED="${STEP_TEST_DIFF_EXPECTED}"
DIFF_ACTUAL="${STEP_TEST_DIFF_ACTUAL}"
DIFF_END="${STEP_TEST_DIFF_END}"
STORAGE_ROOT="\${STEP_TEST_STORAGE_ROOT:-/storage}"
VOLUME_BASE64_SENTINEL="${STEP_TEST_VOLUME_BASE64_SENTINEL}"
VOLUME_SQLITE_DUMP_SENTINEL="${STEP_TEST_SQLITE_DUMP_SENTINEL}"
_STEP_CMD="$(cat ${STEP_COMMAND_FILE})"
_DECLARED_VOLUMES=""
[ -f "${STEP_TEST_VOLUME_LIST_FILE}" ] && _DECLARED_VOLUMES="$(cat "${STEP_TEST_VOLUME_LIST_FILE}")"
_RO_VOLUMES=""
[ -f "${STEP_TEST_RO_VOLUME_LIST_FILE}" ] && _RO_VOLUMES="$(cat "${STEP_TEST_RO_VOLUME_LIST_FILE}")"
_HAS_REQUEST_SERIALIZER=0
[ -f "${STEP_TEST_REQUEST_SERIALIZER_FILE}" ] && _HAS_REQUEST_SERIALIZER=1
_HAS_OUTPUT_SERIALIZER=0
[ -f "${STEP_TEST_OUTPUT_SERIALIZER_FILE}" ] && _HAS_OUTPUT_SERIALIZER=1
_specs=""
_fail=0

${BUN_PRELOAD_SHELL}

_json_escape() {
  printf '%s' "$1" | tr '[:cntrl:]' ' ' | sed 's/\\\\/\\\\\\\\/g; s/"/\\\\"/g'
}

_record() {
  _n=$(_json_escape "$1")
  if [ -n "$3" ]; then _m=$(_json_escape "$3"); else _m=""; fi
  _obj="{\\"name\\":\\"$_n\\",\\"status\\":\\"$2\\",\\"message\\":\\"$_m\\"}"
  if [ -z "$_specs" ]; then _specs="$_obj"; else _specs="$_specs,$_obj"; fi
}

${VOLUME_SHELL_HELPERS}
_OUTPUT_SERIALIZER_ERR="$_SCRATCH_DIR/.step-test-serialize-output-err"

${REPLAY_CTL_SHELL_HELPERS}

${NORMALIZE_HELPER_SHELL}

_asserted_volumes() {
  _root=$(_volume_root "$1")
  for _phase in initial expected; do
    _dir="$_root/$_phase"
    [ -d "$_dir" ] || continue
    for _vol in "$_dir"/*; do
      [ -d "$_vol" ] || continue
      printf '%s\\n' "\${_vol##*/}"
    done
  done | sort -u
}

_prepare_volumes() {
  [ -n "$_DECLARED_VOLUMES" ] || [ -d "tests/step-tests/$1.volumes" ] || return 0
  for _vol in $(
    { printf '%s\\n' $_DECLARED_VOLUMES; _asserted_volumes "$1"; } | sort -u
  ); do
    _clear_volume "$_vol" || return 1
  done
  _seed_volumes "$1"
}

_serialize_output() {
  printf '%s\\n' "$1" | sh ${STEP_TEST_OUTPUT_SERIALIZER_FILE} 2>"$_OUTPUT_SERIALIZER_ERR"
}

_compare_serialized() {
  _vol="$1"
  _snapshot_file="$2"
  if [ ! -f tests/serialize ]; then
    _volume_fail "missing tests/serialize for volume $_vol"
    return
  fi
  _actual_serialized="$_SCRATCH_DIR/.step-test-actual-serialized/$_vol"
  mkdir -p "\${_actual_serialized%/*}"
  if ! _serialize_volume "$_vol" "$_actual_serialized"; then
    _volume_fail "tests/serialize failed for volume $_vol"
  elif ! cmp -s "$_snapshot_file" "$_actual_serialized"; then
    _volume_fail "volume file /storage/$_vol did not match expected content"
  fi
  rm -f "$_actual_serialized"
}

# A volume with an expected/ fixture is compared against it as before; a
# listed read-only volume with no expected/ fixture is instead compared
# against its initial/ fixture, asserting the step left it untouched. A
# volume with an expected/<vol>.serialized snapshot is instead compared via
# the step's tests/serialize output.
_compare_volumes() {
  _volume_message=""
  _root=$(_volume_root "$1")
  [ -d "$_root" ] || return 0
  for _vol in $(_asserted_volumes "$1"); do
    if [ -f "$_root/expected/$_vol.serialized" ]; then continue; fi
    _expected_vol="$_root/expected/$_vol"
    _initial_vol="$_root/initial/$_vol"
    if [ ! -d "$_expected_vol" ] && _is_ro_volume "$_vol" && [ -d "$_initial_vol" ]; then
      _compare_volume_against "$_vol" "$_initial_vol" read-only
    else
      _compare_volume_against "$_vol" "$_expected_vol"
    fi
  done
  for _snapshot_file in "$_root"/expected/*.serialized; do
    [ -f "$_snapshot_file" ] || continue
    _vol=\${_snapshot_file##*/}
    _vol=\${_vol%.serialized}
    _compare_serialized "$_vol" "$_snapshot_file"
  done
  [ -z "$_volume_message" ]
}

_emit() {
  printf '%s {"specs":[%s]}\\n' "$SENTINEL" "$_specs"
  rm -rf "$_SCRATCH_DIR"
  exit "$_fail"
}
trap _emit EXIT

# Replays one test, leaving _outcome as passed or failed with _message; _has_diff marks an output mismatch between _expected and _actual.
_replay_once() {
  _outcome=failed
  _message=""
  _has_diff=0
  if ! _prepare_volumes "$1" || ! _snapshot_read_only_sqlite_files "$1"; then
    _message="could not prepare volume fixtures"
    return
  fi
  _requests_file="tests/step-tests/$1${STEP_TEST_REQUESTS_SUFFIX}"
  _has_requests=0
  [ -f "$_requests_file" ] && _has_requests=1
  if [ "$_has_requests" = 1 ]; then
    _message=$(_check_replay_tools 2>&1) || return
    if [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
      _message=$(_compute_and_register_fixture_keys "$_requests_file" "$1" 2>&1 >/dev/null) || return
    fi
    _message=$(_replay_ctl begin "$1")
    [ -n "$_message" ] && return
  fi
  _use_normalize_helper=0
  if [ "$_has_requests" = 1 ] && [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
    _use_normalize_helper=1
    _normalize_helper_start
  fi
  if _actual=$(sh -c "$_STEP_CMD" < "tests/step-tests/$1.input"); then
    _step_status=0
  else
    _step_status=$?
  fi
  _replay_message=""
  [ "$_has_requests" = 1 ] && _replay_message=$(_replay_ctl end "$1")
  [ "$_use_normalize_helper" = 1 ] && _normalize_helper_stop
  if [ "$_step_status" != 0 ]; then
    _message="step exited with code $_step_status"
    [ -n "$_replay_message" ] && _message="$_message; requests diverged — $_replay_message"
    return
  fi
  _expected=$(cat "tests/step-tests/$1.output")
  if [ "$_HAS_OUTPUT_SERIALIZER" = 1 ]; then
    if ! _expected=$(_serialize_output "$_expected") || ! _actual=$(_serialize_output "$_actual"); then
      _message="${STEP_TEST_OUTPUT_SERIALIZER_FILE} failed: $(head -c 500 "$_OUTPUT_SERIALIZER_ERR")"
      return
    fi
  fi
  if [ "$_expected" != "$_actual" ]; then
    _message="expected [$_expected] but got [$_actual]"
    [ -n "$_replay_message" ] && _message="$_message; requests diverged — $_replay_message"
    _has_diff=1
    return
  fi
  if [ -n "$_replay_message" ]; then
    _message="$_replay_message"
    return
  fi
  if ! _compare_volumes "$1"; then
    _message="$_volume_message"
    return
  fi
  _outcome=passed
}

_found=0
for _input in tests/step-tests/*.input; do
  [ -e "$_input" ] || continue
  _found=1
  _name=\${_input##*/}
  _name=\${_name%.input}
  _out_file="tests/step-tests/$_name.output"
  if [ ! -f "$_out_file" ]; then
    _record "$_name" failed "missing $_out_file"
    _fail=1
    continue
  fi
  _replay_once "$_name"
  if [ "$_outcome" = passed ]; then
    _record "$_name" passed ""
    continue
  fi
  if [ -f "${STEP_TEST_CASES_DIR}/$_name${STEP_TEST_CLOCK_SUFFIX}" ]; then
    _message="$_message ${STEP_TEST_FROZEN_CLOCK_HINT}"
  fi
  _record "$_name" failed "$_message"
  _fail=1
  if [ "$_has_diff" = 1 ]; then
    printf '%s %s\\n' "$DIFF_BEGIN" "$_name"
    printf '%s\\n' "$DIFF_EXPECTED"
    printf '%s\\n' "$_expected"
    printf '%s\\n' "$DIFF_ACTUAL"
    printf '%s\\n' "$_actual"
    printf '%s\\n' "$DIFF_END"
  fi
done

if [ "$_found" = 0 ]; then
  echo "No step tests found under tests/step-tests/. Add one first." >&2
  _fail=1
fi
`;

type StepFile = { path: string; content: string };

// Runs the step once against a single live input, seeding declared volumes
// first, then emits the step’s final stdout and volume bytes as base64 so
// the caller can save them as a step test’s expected fixtures.
export const STEP_TEST_CAPTURE_SH = `#!/bin/sh
SENTINEL="${STEP_TEST_CAPTURE_SENTINEL}"
STORAGE_ROOT="\${STEP_TEST_STORAGE_ROOT:-/storage}"
VOLUME_BASE64_SENTINEL="${STEP_TEST_VOLUME_BASE64_SENTINEL}"
VOLUME_SQLITE_DUMP_SENTINEL="${STEP_TEST_SQLITE_DUMP_SENTINEL}"
_STEP_CMD="$(cat ${STEP_COMMAND_FILE})"
_VOLUMES="$(cat ${STEP_TEST_VOLUME_LIST_FILE})"
_RO_VOLUMES=""
[ -f "${STEP_TEST_RO_VOLUME_LIST_FILE}" ] && _RO_VOLUMES="$(cat "${STEP_TEST_RO_VOLUME_LIST_FILE}")"
_SPEC="${CAPTURE_FIXTURE_SET_NAME}"

${VOLUME_SHELL_HELPERS}
trap 'rm -rf "$_SCRATCH_DIR"' EXIT

${REPLAY_CTL_SHELL_HELPERS}

${NORMALIZE_HELPER_SHELL}

_base64() {
  base64 | tr -d '\\n'
}

for _vol in $_VOLUMES; do
  _clear_volume "$_vol" || exit 1
done
_seed_volumes "$_SPEC" || exit 1
_snapshot_read_only_sqlite_files "$_SPEC" || exit 1

_SERIALIZED_DIR="$_SCRATCH_DIR/.step-test-serialized"
if [ -f tests/serialize ]; then
  mkdir -p "$_SERIALIZED_DIR"
  for _vol in $_RO_VOLUMES; do
    if ! _serialize_volume "$_vol" "$_SERIALIZED_DIR/$_vol.pre"; then
      echo "tests/serialize failed for volume $_vol" >&2
      exit 1
    fi
  done
fi

_requests_file="${STEP_TEST_REQUESTS_FILE}"
_has_requests=0
[ -f "$_requests_file" ] && _has_requests=1
_HAS_REQUEST_SERIALIZER=0
[ -f "${STEP_TEST_REQUEST_SERIALIZER_FILE}" ] && _HAS_REQUEST_SERIALIZER=1

if [ "$_has_requests" = 1 ]; then
  _check_replay_tools || exit 1
fi
if [ "$_has_requests" = 1 ] && [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
  _compute_and_register_fixture_keys "$_requests_file" "${CAPTURE_FIXTURE_SET_NAME}" || exit 1
  printf '%s\\tfixtureKeys\\t%s\\n' "$SENTINEL" "$(printf '%s' "$_fk_keys" | base64 | tr -d '\\n')"
fi
if [ "$_has_requests" = 1 ]; then
  _replay_message=$(_replay_ctl begin "$_SPEC")
  if [ -n "$_replay_message" ]; then
    echo "$_replay_message" >&2
    exit 1
  fi
fi

_use_normalize_helper=0
if [ "$_has_requests" = 1 ] && [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
  _use_normalize_helper=1
  _normalize_helper_start
fi

_output_file="$_SCRATCH_DIR/.step-test-capture-output"
if sh -c "$_STEP_CMD" > "$_output_file"; then
  _status=0
else
  _status=$?
fi

_replay_message=""
[ "$_has_requests" = 1 ] && _replay_message=$(_replay_ctl end "$_SPEC")
[ "$_use_normalize_helper" = 1 ] && _normalize_helper_stop
if [ -n "$_replay_message" ]; then
  echo "${STEP_TEST_REQUESTS_DIVERGED_PREFIX}$_replay_message" >&2
  exit 1
fi

printf '%s\\tstatus\\t%s\\n' "$SENTINEL" "$_status"
printf '%s\\tstdout\\t' "$SENTINEL"
_base64 < "$_output_file"
printf '\\n'

if [ "$_status" = 0 ]; then
  # Two back-to-back serializer runs must agree, so a nondeterministic serializer fails the pin here rather than flaking at replay time.
  if [ -f tests/serialize ]; then
    for _vol in $_VOLUMES; do
      _post1="$_SERIALIZED_DIR/$_vol.post1"
      _post2="$_SERIALIZED_DIR/$_vol.post2"
      if ! _serialize_volume "$_vol" "$_post1" || ! _serialize_volume "$_vol" "$_post2"; then
        echo "tests/serialize failed for volume $_vol" >&2
        exit 1
      fi
      if ! cmp -s "$_post1" "$_post2"; then
        echo "serializer for volume $_vol produced different output on two runs; make tests/serialize deterministic" >&2
        exit 1
      fi
      if _is_ro_volume "$_vol" && ! cmp -s "$_SERIALIZED_DIR/$_vol.pre" "$_post1"; then
        echo "step wrote to read-only volume $_vol" >&2
        exit 1
      fi
      printf '%s\\tserialized\\t%s\\t' "$SENTINEL" "$_vol"
      _base64 < "$_post1"
      printf '\\n'
    done
  else
    for _vol in $_RO_VOLUMES; do
      _initial_vol="$(_volume_root "$_SPEC")/initial/$_vol"
      [ -d "$_initial_vol" ] || continue
      _volume_message=""
      _compare_volume_against "$_vol" "$_initial_vol" read-only
      if [ -n "$_volume_message" ]; then
        echo "step wrote to read-only volume $_vol: $_volume_message" >&2
        exit 1
      fi
    done
    for _vol in $_VOLUMES; do
      _is_ro_volume "$_vol" && continue
      _root="$STORAGE_ROOT/$_vol"
      [ -d "$_root" ] || continue
      find "$_root" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort | while IFS= read -r _file; do
        _rel=\${_file#$_root/}
        _dump_out="$_SCRATCH_DIR/.step-test-capture-dump"
        if _is_sqlite_file "$_file" && _sqlite_canonical_dump "$_file" "$_dump_out"; then
          printf '%s\\tsqlitedump\\t%s\\t%s\\t' "$SENTINEL" "$_vol" "$_rel"
          _base64 < "$_dump_out"
          rm -f "$_dump_out"
        else
          printf '%s\\tfile\\t%s\\t%s\\t' "$SENTINEL" "$_vol" "$_rel"
          _base64 < "$_file"
        fi
        printf '\\n'
      done
    done
  fi
fi

exit "$_status"
`;

export function assembleStepTestCaptureFiles(args: {
  codeFiles: StepFile[];
  testFiles: StepFile[];
  initialVolumeFiles: StepFile[];
  stepCommand: string;
  volumes: readonly string[];
  roVolumes: readonly string[];
  requestFixtures?: string;
}): StepFile[] {
  return [
    ...args.codeFiles,
    ...args.testFiles,
    ...args.initialVolumeFiles,
    { path: STEP_TEST_CAPTURE_RUNNER_FILE, content: STEP_TEST_CAPTURE_SH },
    { path: STEP_COMMAND_FILE, content: args.stepCommand },
    { path: STEP_TEST_VOLUME_LIST_FILE, content: args.volumes.join("\n") },
    { path: STEP_TEST_RO_VOLUME_LIST_FILE, content: args.roVolumes.join("\n") },
    ...(args.requestFixtures !== undefined
      ? [{ path: STEP_TEST_REQUESTS_FILE, content: args.requestFixtures }]
      : []),
  ];
}

export type StepTestCaptureResult = {
  status: number;
  stdout: string;
  volumeFiles: (Omit<StepTestVolumeFixtureFile, "encoding"> & {
    encoding: Exclude<
      NonNullable<StepTestVolumeFixtureFile["encoding"]>,
      "utf8"
    >;
  })[];
  /** Per-fixture serializer match keys, in recorded order, when tests/serialize-request exists. */
  requestFixtureKeys?: string[];
};

export function parseStepTestCapture(output: string): StepTestCaptureResult {
  let status: number | null = null;
  let stdout: string | null = null;
  const volumeFiles: StepTestCaptureResult["volumeFiles"] = [];
  let requestFixtureKeys: string[] | undefined;
  for (const line of output.split("\n")) {
    if (!line.startsWith(`${STEP_TEST_CAPTURE_SENTINEL}\t`)) continue;
    const parts = line.split("\t");
    const kind = parts[1];
    if (kind === "status") {
      status = Number.parseInt(parts[2] ?? "", 10);
    } else if (kind === "stdout") {
      stdout = Buffer.from(parts[2] ?? "", "base64").toString("utf-8");
    } else if (kind === "fixtureKeys") {
      requestFixtureKeys = JSON.parse(
        Buffer.from(parts[2] ?? "", "base64").toString("utf-8")
      );
    } else if (kind === "file") {
      volumeFiles.push({
        volume: parts[2] ?? "",
        path: parts[3] ?? "",
        content: parts[4] ?? "",
        encoding: "base64",
      });
    } else if (kind === "sqlitedump") {
      volumeFiles.push({
        volume: parts[2] ?? "",
        path: parts[3] ?? "",
        content: Buffer.from(parts[4] ?? "", "base64").toString("utf-8"),
        encoding: "sqlite-dump",
      });
    } else if (kind === "serialized") {
      const volume = parts[2] ?? "";
      const bytes = Buffer.from(parts[3] ?? "", "base64");
      if (!bytesAreUtf8Text(bytes)) {
        throw new Error(
          `serializer output for volume ${volume} is not valid UTF-8 text; make tests/serialize print text`
        );
      }
      volumeFiles.push({
        volume,
        path: "",
        content: bytes.toString("utf-8"),
        encoding: "serialized",
      });
    }
  }
  if (status === null || stdout === null) {
    throw new Error(
      "Step test capture output was missing its sentinel payload"
    );
  }
  return {
    status,
    stdout,
    volumeFiles,
    ...(requestFixtureKeys !== undefined ? { requestFixtureKeys } : {}),
  };
}
